import { Formik, FieldArray, Field as FormikField, Form } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { handbookSelectors, handbookThunks } from '@/entity/handbook'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, DataGridTable, Field, Title } from '@/ui'
import { FormControlLabel, RadioGroup } from '@mui/material'
import { Radio } from '@/entity/contracts/ui/form/components'
import { BACKEND_FILE_TYPE, FILE_FORMAT } from '@app/config'
import { Uploader } from '@/components/Uploader/Uploader'
import { getCurrentLanguage } from '@/i18next'
import { LANGUAGE_ENUM } from '@/shared'
import styles from '../accidentReport/Form.module.scss'
import cl from 'classnames'
import { DigitalSign } from '@/utils/digitalSign'
import { accidentThunks } from '@/entity'
import dayjs from 'dayjs'
import { message } from 'antd'
import { Loader, LoadingBlocker } from '@/components/Loader'


const FORM_NAME = {
	accidentId: 'accidentId',
	bin: 'bin',
	conclusions: 'conclusions',
	fileDate: 'fileDate',
	fileId: 'fileId',
	fileName: 'fileName',
	injuredExternalId: 'injuredExternalId',
	mimeType: 'mimeType',
	severityDegreeCode: 'severityDegreeCode',
	incidentTypeCode: 'incidentTypeCode',
}

export const ConclusionSeverity = ({accident, onClose}) => {
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const lang = getCurrentLanguage()
	const digitalSign = new DigitalSign()

	const { incidentType, severityDegree, activeCompany, setSeverity } = useSelector((state) => ({
		incidentType: state.handbook.incidentType,
		severityDegree: state.handbook.severityDegree,
		activeCompany: state.company.company,
		setSeverity: state.accidents.setSeverity
	}))

	const formikInitialState = {
		incidentTypeCode: accident?.incidentTypeCode || null,
		accidentId: accident.externalId,
		bin: activeCompany.bin,
		conclusions: accident?.injureds?.map(i => ({
			fileDate: i.severity?.fileDate ? dayjs(i.severity?.fileDate, 'YYYY-MM-DD').format('DD.MM.YYYY') : null,
			fileId: i.severity?.fileId || null,
			fileName: i.severity?.fileName || null,
			injuredExternalId: i.severity?.injuredExternalId || i.externalId,
			mimeType: i.severity?.mimeType || null,
			severityDegreeCode: i.severity?.severityDegreeCode?.code || null,
		})),
	}

	const [isEdit, setIsEdit] = useState(formikInitialState?.incidentTypeCode)

	const incidentTypeArrayGroup = incidentType?.filter(i => i.code === 'one' || i.code === 'two' || i.code === 'fourteen')
	const incidentTypeArraySingle = incidentType?.filter(i => i.code === 'three' || i.code === 'four' || i.code === 'five' || i.code === 'six')

	const incidentTypeArray = accident.isMoreOne ? incidentTypeArrayGroup : incidentTypeArraySingle

	const startDate = accident.injureds?.map(i => i?.severity?.createdDate)?.reduce((accumulator, curVal) =>
		curVal && dayjs(accumulator).isBefore(curVal) ? accumulator : curVal,
	)

	useEffect(() => {
		if (!incidentType?.length > 0) dispatch(handbookThunks.getIncidentType())
		if (!severityDegree?.length > 0) dispatch(handbookThunks.getSeverityDegree())
	}, [])

	const onSubmit = (data) => {
		let body = {
			data: { ...data, conclusions: data.conclusions?.map(i => ({...i, fileDate: dayjs(i.fileDate, 'DD.MM.YYYY').format('YYYY-MM-DD')})) },
			onSuccess: async (xml) => {
				let res = await digitalSign.handleSend(xml.xmlForSigned)
				let innerBody = {
					data: {
						signedXml: res,
						externalId: data.accidentId
					},
					onSuccess: () => {
						onClose()
						message.success('Успешно!')
					},
					onFailed: () => {

					}
				}
				dispatch(accidentThunks.sendSeverity(innerBody))
			},
			onFailed: () => {

			},
		}

		dispatch(accidentThunks.sendSeverityXml(body))
	}

	const onErrorDocument = (error) => {
		console.log('error', error)
	}

	const category = (incidentType || [])?.find(i => i?.code === formikInitialState?.incidentTypeCode)

	const categoryTitle = {
		[LANGUAGE_ENUM.rus]: category?.nameRu,
		[LANGUAGE_ENUM.kz]: category?.nameKz
	}

	const setData = (row) => {
		let severity = {
			[LANGUAGE_ENUM.kz]: row?.severity?.severityDegreeCode?.nameKz,
			[LANGUAGE_ENUM.rus]: row?.severity?.severityDegreeCode?.nameRu,
		}
		return [
			{
				title: t('accidentsPage.severity'),
				data: severity[lang] || '',
			},
			{
				title: t('accidentsPage.doc'),
				data: row?.severity?.fileName,
			},
			{
				title: t('accidentsPage.conclusionSeverity.doc_date'),
				data: row?.severity?.fileDate,
			}
		]
	}

	return (
		<>
			{isEdit ? (
				<section style={{ padding: '32px 64px'}} className={cl('flex', 'flex-column', 'gap-4')}>
					<div>
						<Title type={'h5'} color={'darkBlue'}>
							<strong>{t('accidentsPage.conclusionSeverity.category')}</strong>: {categoryTitle[lang] || ''}
						</Title>
					</div>

					<div className={cl('flex-1')}>
						{accident?.injureds?.map(i => {
							const fullPersonName = (i.surname || '') + ' ' + (i.firstname || '') + ' ' + (i.secondname || '')
							return (
								<DataGridTable
									key={i.externalId}
									customTitle={fullPersonName}
									hideTitle
									hideSubTitle
									information={setData(i)}
									titleClass={styles.tableTitleCell}
									rowClass={styles.ml}
								/>
							)
						})}
					</div>

					<div>
						<Button onClick={() => {
							if(formikInitialState.incidentTypeCode) setIsEdit(prev => !prev)
						}}>
							{t('actions.edit')}
						</Button>
					</div>

					<div>
						<span><strong><i>{t('accidentsPage.startDate', { date: startDate })}</i></strong></span>
					</div>
				</section>
			): (
				<Formik
					initialValues={formikInitialState}
					onSubmit={onSubmit}
				>
					<Form className={cl(styles.wrapper, 'flex', 'flex-column', 'gap-3')}>
						<div>
							<FormikField name={FORM_NAME.incidentTypeCode}>
								{({ field, meta: { touched, error } }) => (
									<Field
										{...field}
										options={incidentTypeArray}
										type="text"
										fieldType="select"
										label={t('accidentsPage.conclusionSeverity.category')}
										placeholder={t('accidentsPage.conclusionSeverity.category_placeholder')}
										isRequired
										disabled={false}
										error={touched && error}
									/>
								)}
							</FormikField>
						</div>

						<div>
							<FieldArray name={FORM_NAME.conclusions}>
								{({ form, push, remove, ...props }) => {
									return (
										<div className={cl('flex', 'flex-column', 'gap-3')}>
											{
												form.values.conclusions.map((i, idx) => (
													<div key={idx} className={cl('flex', 'flex-column', 'gap-3')}>
														<div>
															<span><strong>{accident?.injureds?.[idx]?.surname || ''} {accident?.injureds?.[idx]?.firstname || ''} {accident?.injureds?.[idx]?.secondname || ''}</strong></span>
														</div>
														<FormikField name={`${FORM_NAME.conclusions}.${idx}.${FORM_NAME.severityDegreeCode}`}>
															{({ field, form: { errors }, name, options, children, ...props }) => {
																return (
																	<div>
																		<span>{t('accidentsPage.severity')}</span>
																		<RadioGroup {...field} classes={{
																			root: styles.label__root
																		}}>
																			{severityDegree.map((severity, severityIdx) => {
																				let label = {
																					[LANGUAGE_ENUM.kz]: severity.nameKz,
																					[LANGUAGE_ENUM.rus]: severity.nameRu,
																				}
																				return (
																					<FormControlLabel
																						key={severity.code}
																						control={<Radio />}
																						label={label[lang]}
																						value={severity.code}
																					/>
																				)
																			})}
																		</RadioGroup>
																	</div>
																)
															}}
														</FormikField>

														<div>
															<span>{t('accidentsPage.conclusionSeverity.med_doc')}</span>
															<Uploader
																backendFileType={BACKEND_FILE_TYPE.COLLECTIVECONTRACT}
																acceptedFileTypes={[FILE_FORMAT.jpg, FILE_FORMAT.jpeg, FILE_FORMAT.png, FILE_FORMAT.pdf]}
																value={i.fileId ? [{id: i.fileId, fileName: i.fileName, fileType: i.mimeType}] : []}
																onSuccess={(values) => {
																	form.setFieldValue(`${FORM_NAME.conclusions}.${idx}.${FORM_NAME.fileId}`, values?.[0]?.id)
																	form.setFieldValue(`${FORM_NAME.conclusions}.${idx}.${FORM_NAME.fileName}`, values?.[0]?.fileName)
																	form.setFieldValue(`${FORM_NAME.conclusions}.${idx}.${FORM_NAME.mimeType}`, values?.[0]?.fileType)
																}}
																onError={(error) => onErrorDocument(error)}
																maxFiles={1}
															/>
														</div>

														<div className={cl('flex')}>
															<FormikField name={`${FORM_NAME.conclusions}.${idx}.${FORM_NAME.fileDate}`}>
																{({ field, meta: { touched, error } }) => (
																	<Field
																		{...field}
																		fieldType="datePicker"
																		label={t('accidentsPage.conclusionSeverity.doc_date')}
																		placeholder={t('default_date_value')}
																		error={touched && error}
																	/>
																)}
															</FormikField>
														</div>
													</div>
												))
											}
										</div>
									)
								}}
							</FieldArray>
						</div>

						<div className={cl('flex', 'gap-5', 'align-center', 'justify-content-end', 'mt-4')}>
							{formikInitialState.incidentTypeCode &&
								<Button type="bare" onClick={() => {
									if(formikInitialState.incidentTypeCode) setIsEdit(prev => !prev)
								}}>
									{t('cancel')}
								</Button>
							}

							<Button type="submit">
								{t('labor_records.sign_and_send')}
							</Button>
						</div>
					</Form>
				</Formik>
				)}

			{setSeverity.loading && <LoadingBlocker />}
		</>
	)
}